<template>
  <div class="tw:flex tw:flex-col tw:lg:flex-row tw:gap-6 tw:mt-0">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="tw:flex tw:items-center tw:justify-center tw:py-8 tw:w-full"
    >
      <div class="tw:text-center">
        <Loader2
          class="tw:animate-spin tw:w-8 tw:h-8 tw:text-zinc-500 tw:mb-4 tw:mx-auto"
        />
        <p class="tw:text-gray-600">{{ $t("Loading product test…") }}</p>
      </div>
    </div>

    <div class="tw:flex-1 tw:border-t tw:lg:flex tw:divide-y tw:lg:divide-y-0 tw:lg:divide-x tw:-m-6 tw:mt-0">
      <div v-if="!isLoading" class="tw:flex-1 tw:p-6 tw:lg:max-w-lg">
        <div v-if="productTest" class="tw:space-y-6 tw:lg:space-y-4">
          <div class="card">
            <div class="card-body tw:p-0">
              <!-- Registration -->
              <template v-if="productTest.registration">
                <RegistrationInfo class="tw:p-3" :registration="productTest.registration" />
              </template>

              <!-- Item -->
              <EquipmentInfo v-if="productTest.equipment" :item="productTest.equipment" class="tw:border-t tw:p-3" />
            </div>
          </div>

          <template v-if="productTest.end_at == null || productTest.expected_at || productTest.comment">
            <div class="card tw:overflow-hidden">
              <div class="card-body tw:p-0">
                <!-- Expected return date -->
                <div>
                  <!-- Display mode when date exists and not editing -->
                  <div
                    v-if="productTest.expected_at && !isEditingExpectedAt"
                    class="tw:p-3"
                  >
                    <div class="tw:flex tw:items-start tw:gap-3">
                      <CalendarArrowUp
                        class="tw:size-5 tw:mt-0.5 tw:flex-shrink-0"
                      />
                      <div class="tw:flex-1">
                        <h3 class="tw:text-base tw:mb-0">
                          {{ $t("Expected return date") }}
                        </h3>
                        <p class="mb-0">
                      <span
                        :class="[
                          'badge badge-pill ',
                          productTest.local_end_at ? 'badge-dark' : '',
                          productTest.status === ProductTestStatusEnum.LATE ? 'badge-danger' : '',
                          productTest.status === ProductTestStatusEnum.IN_PROGRESS ? 'badge-info' : '',
                        ]"
                      >
                        <i class="fa fa-flag-checkered" aria-hidden="true"></i>
                        {{ dayjs(productTest.expected_at).format("lll") }}
                      </span>
                        </p>
                        <button
                          v-if="productTest.end_at == null"
                          @click="startEditingExpectedAt"
                          class="btn btn-sm btn-outline-secondary tw:mt-3"
                        >
                          {{ $t("Change") }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Edit mode - unified form for both new and existing dates -->
                  <div v-else-if="isEditingExpectedAt" class="tw:p-3">
                    <div class="tw:flex tw:items-start tw:gap-3">
                      <CalendarArrowUp
                        class="tw:size-5 tw:mt-0.5 tw:flex-shrink-0"
                      />
                      <div class="tw:flex-1">
                        <label
                          class="tw:font-bold tw:mb-1 tw:block"
                          for="frmExpectedAt"
                        >{{ $t("Expected return date") }}</label
                        >
                        <Datetime
                          id="frmExpectedAt"
                          v-model="editingExpectedAt"
                          :min="minDateTime"
                          :timezone="productTest.tour?.timezone"
                          with-timezone
                          :class-name="errors.expected_at ? 'is-invalid' : ''"
                        ></Datetime>
                        <div v-if="errors.expected_at" class="text-danger small">
                          {{ errors.expected_at.join(", ") }}
                        </div>

                        <div class="tw:flex tw:justify-between tw:mt-3">
                          <div class="tw:flex tw:gap-2">
                            <button
                              @click="saveExpectedAt"
                              class="btn btn-sm btn-primary"
                            >
                              {{ $t("Save") }}
                            </button>
                            <button
                              @click="cancelEditingExpectedAt"
                              class="btn btn-sm btn-outline-secondary"
                            >
                              {{ $t("Cancel") }}
                            </button>
                          </div>
                          <button
                            @click="resetExpectedAt"
                            class="btn btn-sm btn-ghost text-danger tw:hover:bg-zinc-50"
                            :disabled="!editingExpectedAt"
                          >
                            {{ $t("Reset") }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Set date button when no expected date and not editing -->
                  <div
                    v-if="
                  !productTest.expected_at &&
                  !isEditingExpectedAt &&
                  productTest.end_at == null
                "
                  >
                    <button
                      @click="startEditingExpectedAt"
                      class="tw:block tw:w-full tw:bg-white-500 tw:hover:bg-zinc-50/50 tw:flex tw:items-center tw:gap-2 tw:p-3 tw:text-blue-600"
                    >
                      <CalendarArrowUp class="tw:size-5" />
                      {{ $t("Set expected return date") }}
                    </button>
                  </div>
                </div>

                <!-- Comment -->
                <div :class="{
                  'tw:border-t': productTest.end_at == null || (productTest.expected_at && productTest.comment),
                }">
                  <div v-if="productTest.comment || isEditingComment">
                    <div class="tw:p-3">
                      <div class="tw:flex tw:items-start tw:gap-3">
                        <MessageSquare
                          class="tw:size-5 tw:mt-0.5 tw:flex-shrink-0"
                        />
                        <div class="tw:flex-1">
                          <!-- Display mode -->
                          <div v-if="!isEditingComment">
                            <h3 class="tw:text-base tw:mb-1">
                              {{ $t("Comment") }}
                            </h3>
                            <p class="mb-0">{{ productTest.comment }}</p>
                            <button
                              v-if="productTest.end_at == null"
                              @click="startEditingComment"
                              class="btn btn-sm btn-outline-secondary tw:mt-3"
                            >
                              {{ $t("Edit") }}
                            </button>
                          </div>

                          <!-- Edit mode -->
                          <div v-else>
                            <label
                              class="tw:font-bold tw:mb-1 tw:block"
                              for="comment"
                            >{{ $t('Comment') }}</label
                            >
                            <textarea
                              id="comment"
                              class="form-control"
                              v-model="editingComment"
                              rows="3"
                              :class="errors.comment ? 'is-invalid' : ''"
                              :placeholder="$t('Enter your comment here…')"
                            ></textarea>
                            <div v-if="errors.comment" class="text-danger small">
                              {{ errors.comment.join(", ") }}
                            </div>

                            <div class="tw:flex tw:justify-between tw:mt-3">
                              <div class="tw:flex tw:gap-2">
                                <button
                                  @click="saveComment"
                                  class="btn btn-sm btn-primary"
                                >
                                  {{ $t("Save") }}
                                </button>
                                <button
                                  @click="cancelEditingComment"
                                  class="btn btn-sm btn-outline-secondary"
                                >
                                  {{ $t("Cancel") }}
                                </button>
                              </div>
                              <button
                                @click="resetComment"
                                class="btn btn-sm btn-ghost text-danger"
                                :disabled="!editingComment"
                              >
                                {{ $t("Reset") }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Add comment button when no comment exists and not editing -->
                  <div
                    v-if="
                  !productTest.comment &&
                  !isEditingComment &&
                  productTest.end_at == null
                "
                  >
                    <button
                      @click="startEditingComment"
                      class="tw:block tw:w-full tw:bg-white-500 tw:hover:bg-zinc-50/50 tw:flex tw:items-center tw:gap-2 tw:p-3 tw:text-blue-600"
                    >
                      <MessageSquare class="tw:size-5 tw:mr-1" />
                      {{ $t("Add comment") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <div v-if="productTest.end_at == null">
            <CloseProductTest
              :productTest="productTest"
              v-on:close="refresh"
            ></CloseProductTest>
          </div>

          <template v-if="productTest?.end_at !== null">
            <div class="card">
              <div class="card-body tw:p-3">
                <div class="tw:flex tw:items-start tw:gap-3">
                  <MessagesSquare class="tw:size-5 tw:mt-0.5 tw:flex-shrink-0" />
                  <div class="tw:flex-1">
                    <h3 class="tw:text-base tw:mb-2 tw:flex tw:items-center tw:gap-1">
                      {{ $t("Review") }}
                      <template
                        v-if="
                          productTest.productReview &&
                          productTest.productReview.links.show
                        "
                      >
                        <a
                          :href="productTest.productReview.links.show"
                          target="_blank"
                          class="tw:text-blue-600 p-1 rounded tw:hover:bg-zinc-100"
                        >
                          <SquareArrowOutUpRight class="tw:size-3" />
                        </a>
                      </template>
                    </h3>
                    <template
                      v-if="
                      productTest.productReview &&
                      productTest.productReview.links.show
                    "
                    >
                      <div class="tw:space-y-2">
                        <Rating
                          class="tw:text-xl"
                          :rating-value="productTest.productReview.rating"
                        ></Rating>

                        <template v-if="productTest.productReview.description">
                          <div>
                            {{productTest.productReview.description}}
                          </div>
                        </template>

                        <template v-if="productTest.productReview.created_at">
                          <div class="text-muted tw:text-sm">
                            {{ dayjs(productTest.productReview.created_at).format('lll') }}
                          </div>
                        </template>
                      </div>
                    </template>
                    <template v-else-if="productTest.productReview">
                      <Alert variant="warning">
                        {{ $t("You are not allowed to read this review") }}
                      </Alert>
                    </template>
                    <template v-else>
                      <a
                        :href="productTest.links.addReviewForm"
                        class="btn btn-outline-primary"
                        target="_blank"
                      >
                        {{ $t("Add Review") }}
                      </a>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <template
              v-if="
              productTest.marketingMessages &&
              productTest.marketingMessages.length > 0
            "
            >
              <SendMarketingMessage
                :product-test="productTest"
                :messages="productTest.marketingMessages"
              />
            </template>
          </template>
        </div>
      </div>

      <div v-if="!isLoading" class="tw:flex-1">
        <div class="tw:relative tw:h-full tw:overflow-auto">
          <div class="tw:lg:absolute tw:w-full tw:isolate">
            <h5 class="tw:m-0 tw:text-base tw:lg:sticky tw:lg:z-10 tw:lg:top-0 tw:lg:bg-zinc-50/30 tw:lg:backdrop-blur-xl tw:px-6 tw:pt-6 tw:pb-2 tw:lg:py-4">{{ $t("Activity") }}</h5>

            <ActivityLogForSubject
              v-if="activityLogSubject"
              class="tw:p-6 tw:pt-2 tw:relative tw:z-0"
              :subject="activityLogSubject"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import CloseProductTest from "./CloseProductTest.vue";
import ProductTestModel from "@/models/ProductTest";
import SendMarketingMessage from "./SendMarketingMessage.vue";
import ActivityLogForSubject from "@/components/activity-log/ActivityLogForSubject.vue";
import { emit as emitEvent } from "@/eventBus.js";
import dayjs from "dayjs";
import Alert from "@/components/ui/Alert.vue";
import Rating from "@/components/Rating.vue";
import Datetime from "@/components/ui/Datetime.vue";
import {
  SquareArrowOutUpRight,
  Loader2,
  MessageSquare,
  MessagesSquare,
  CalendarArrowUp,
} from "@lucide/vue";
import RegistrationInfo from "@/components/registration/RegistrationInfo.vue";
import EquipmentInfo from "@/components/equipment/EquipmentInfo.vue";
import { ProductTestStatusEnum } from "@/types/enums";
import type { ProductTest, ProductTestModelInstance } from "@/types/product-test";

interface Props {
  productTestId: number | string | null;
}

const props = withDefaults(defineProps<Props>(), {
  productTestId: null,
});

const productTest = ref<ProductTestModelInstance | null>(null);
const isEditingExpectedAt = ref(false);
const editingExpectedAt = ref<Date | undefined>();
const errors = ref<Record<string, string[]>>({});
const isEditingComment = ref(false);
const editingComment = ref("");
const isLoading = ref(true);

const activityLogSubject = computed(() => {
  if (!productTest.value) {
    return null;
  }
  return "App\\Models\\ProductTest:" + productTest.value.id;
});

const minDateTime = computed(() => {
  return new Date().toISOString();
});

const emit = defineEmits(["refresh"]);
const refresh = () => {
  getProductTest(props.productTestId);
  emitEvent("refresh-activity-log");
  emit("refresh");
};

/**
 * `@/models/ProductTest` is untyped JavaScript, and vue-api-query cannot infer a subclass
 * through its static helpers. The boundary is narrowed here rather than at every call site.
 */
const findProductTest = (id: Props["productTestId"]): Promise<{ data: ProductTest }> =>
  (ProductTestModel as any).find(id);

const getProductTest = (id: Props["productTestId"]) => {
  isLoading.value = true;
  findProductTest(id)
    .then((response) => {
      productTest.value = new ProductTestModel(response.data) as unknown as ProductTestModelInstance;
      isLoading.value = false;
    })
    .catch((error) => {
      isLoading.value = false;
      console.error("Error loading product test:", error);
    });
};

const startEditingExpectedAt = () => {
  if (!productTest.value) {
    return;
  }

  isEditingExpectedAt.value = true;
  editingExpectedAt.value = productTest.value.expected_at
    ? dayjs(productTest.value.expected_at).toDate()
    : undefined;
  errors.value = {};
};

const cancelEditingExpectedAt = () => {
  isEditingExpectedAt.value = false;
  editingExpectedAt.value = undefined;
  errors.value = {};
};

const saveExpectedAt = () => {
  if (!productTest.value) {
    return;
  }

  // Update the productTest model with new values
  productTest.value.expected_at = editingExpectedAt.value
    ? dayjs(editingExpectedAt.value).utc().toISOString()
    : null;

  // Add action parameter for the backend
  productTest.value.action = "update";

  productTest.value
    .save()
    .then(() => {
      isEditingExpectedAt.value = false;
      editingExpectedAt.value = undefined;
      errors.value = {};
      emitEvent("refresh-activity-log");
      // emit refresh to parent
      emit("refresh");
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        errors.value = error.response.data.errors;
      }
    });
};

const resetExpectedAt = () => {
  // Set the date to null for resetting
  editingExpectedAt.value = undefined;
  errors.value = {};
};

const startEditingComment = () => {
  if (!productTest.value) {
    return;
  }

  isEditingComment.value = true;
  editingComment.value = productTest.value.comment || "";
  errors.value = {};
};

const cancelEditingComment = () => {
  isEditingComment.value = false;
  editingComment.value = "";
  errors.value = {};
};

const saveComment = () => {
  if (!productTest.value) {
    return;
  }

  // Update the productTest model with new values
  productTest.value.comment = editingComment.value;

  // Add action parameter for the backend
  productTest.value.action = "update";

  productTest.value
    .save()
    .then(() => {
      isEditingComment.value = false;
      editingComment.value = "";
      errors.value = {};
      emitEvent("refresh-activity-log");
      // emit refresh to parent
      emit("refresh");
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        errors.value = error.response.data.errors;
      }
    });
};

const resetComment = () => {
  // Set the comment to empty string for resetting
  editingComment.value = "";
  errors.value = {};
};

onMounted(() => {
  getProductTest(props.productTestId);
});
</script>

<style scoped></style>
